import React, { useContext, useState, useRef, useEffect } from "react";
import {
  LoadingOverlay,
  Button,
  Paper,
  Text,
  Box,
  Stack,
  Group,
  ThemeIcon,
  Loader,
  useMantineTheme,
} from "@mantine/core";
import {
  IconId,
  IconSunHigh,
  IconCapture,
  IconShieldX,
  IconArrowRight,
  IconCreditCard,
} from "@tabler/icons-react";
import { isMobile as isMobileDevice, isFirefox, isTablet } from "react-device-detect";
import Lottie from "react-lottie-player";
import FaeVerificationAnimation from "../../../assets/fae-verification.json";
import { DataContext } from "../../../context/DataContext";
import { uploadImageToS3 } from "../../../helpers";
import { requestCaptureChallenge, processAndUploadWithIntegrity } from "../../../utils/captureIntegrity";
import FirefoxCameraWarning from "../../../components/FirefoxCameraWarning";
import DocumentCapture from "./capture";
import useStatusCheck from "../../../hooks/useStatusCheck";
import useEdgeStatusCheck from "../../../hooks/useEdgeStatusCheck";
import useLocale from "../../../hooks/useLocale";
import useDeviceDetection from "../../../hooks/useDeviceDetection";
import { getDeviceSignalsUrl } from "../../../utils/edge";
import Handoff from "../../handoff";
import CenterLoadingOverlay from "../../../components/center-loading-overlay";
import StepperLoader from "../../../components/stepper-loader";
import VerificationLoadingScreen from "../../../components/verification-loading-screen";
import CompactHandoff from "../../../components/compact-handoff";

// ── Processing screen ──

const ProcessingScreen = ({ locale, isVeratadSite }) => {
  const [stage, setStage] = useState(0);
  const [showSpinner, setShowSpinner] = useState(false);

  const stages = [
    {
      title: locale?.processing?.uploading || "Uploading...",
      subtitle: locale?.processing?.uploadingSubtitle || "Sending your document for verification",
    },
    {
      title: locale?.processing?.verifying || "Verification in Progress",
      subtitle: locale?.processing?.verifyingSubtitle || "Analyzing your document...",
    },
    {
      title: locale?.processing?.completing || "Completing Verification",
      subtitle: locale?.processing?.completingSubtitle || "Almost done...",
    },
  ];

  useEffect(() => {
    const timer1 = setTimeout(() => setStage(1), 2000);
    const timer2 = setTimeout(() => setStage(2), 4000);
    const timer3 = setTimeout(() => setShowSpinner(true), 6500);
    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
    };
  }, []);

  if (isVeratadSite) {
    const verificationSteps = [
      { title: "Document Analysis", description: "Analyzing document structure and format" },
      { title: "Security Check", description: "Verifying security features and authenticity" },
      { title: "Data Extraction", description: "Extracting and processing personal information" },
      { title: "Final Verification", description: "Cross-referencing data with secure databases" },
    ];

    return (
      <CenterLoadingOverlay
        content={
          <StepperLoader
            title="Verifying Identity Document"
            steps={verificationSteps}
            stepDuration={1500}
            onComplete={() => {}}
          />
        }
      />
    );
  }

  return (
    <Box
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height: "100%",
        minHeight: "400px",
        padding: "20px",
      }}
    >
      {showSpinner ? (
        <Box
          style={{
            width: 300,
            height: 300,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            animation: "fadeIn 0.5s ease-in-out",
          }}
        >
          <Loader size={60} />
        </Box>
      ) : (
        <Lottie
          animationData={FaeVerificationAnimation}
          loop={false}
          play
          style={{ width: 300, height: 300 }}
        />
      )}
      <Box
        style={{
          minHeight: "80px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Text fz={24} fw={700} align="center" mt="md" key={stage}
          style={{ transition: "opacity 0.5s ease-in-out", animation: "fadeIn 0.5s ease-in-out" }}
        >
          {stages[stage].title}
        </Text>
        <Text fz="md" c="dimmed" align="center" mt="xs" key={`sub-${stage}`}
          style={{ transition: "opacity 0.5s ease-in-out", animation: "fadeIn 0.5s ease-in-out" }}
        >
          {stages[stage].subtitle}
        </Text>
      </Box>
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </Box>
  );
};

// ── Front→Back transition screen ──

const FlipTransitionScreen = ({ locale }) => {
  const [step, setStep] = useState(0); // 0=front shown, 1=flipping, 2=back shown

  useEffect(() => {
    const t1 = setTimeout(() => setStep(1), 800);
    const t2 = setTimeout(() => setStep(2), 2200);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);

  return (
    <Box
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 10000,
        backgroundColor: "#111",
        gap: 24,
      }}
    >
      <style>{`
        @keyframes flipCard {
          0%   { transform: rotateY(0deg); }
          50%  { transform: rotateY(90deg); }
          100% { transform: rotateY(180deg); }
        }
        @keyframes fadeSlideIn {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      {/* Card flip animation */}
      <Box
        style={{
          perspective: 600,
          width: 160,
          height: 100,
        }}
      >
        <Box
          style={{
            width: "100%",
            height: "100%",
            borderRadius: 12,
            border: "2px solid rgba(255,255,255,0.3)",
            backgroundColor: "rgba(255,255,255,0.08)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            animation: step >= 1 ? "flipCard 1.2s ease-in-out forwards" : "none",
            transformStyle: "preserve-3d",
          }}
        >
          {step < 2 ? (
            <IconCreditCard size={48} color="rgba(255,255,255,0.7)" stroke={1.2} />
          ) : (
            <Box style={{ transform: "rotateY(180deg)" }}>
              <IconCreditCard size={48} color="rgba(255,255,255,0.7)" stroke={1.2} />
            </Box>
          )}
        </Box>
      </Box>

      {/* Status indicators */}
      <Group spacing="md" style={{ animation: "fadeSlideIn 0.5s ease-out" }}>
        <Box style={{ textAlign: "center", opacity: step >= 2 ? 0.5 : 1, transition: "opacity 0.4s" }}>
          <Text c="white" fz={13} fw={600}>
            {locale?.transition?.front || "FRONT"}
          </Text>
          <Text c="dimmed" fz={11}>✓ {locale?.transition?.captured || "Captured"}</Text>
        </Box>

        <IconArrowRight size={20} color="rgba(255,255,255,0.5)" />

        <Box style={{ textAlign: "center", opacity: step >= 2 ? 1 : 0.5, transition: "opacity 0.4s" }}>
          <Text c="white" fz={13} fw={600}>
            {locale?.transition?.back || "BACK"}
          </Text>
          <Text c="dimmed" fz={11}>{locale?.transition?.next || "Up next"}</Text>
        </Box>
      </Group>

      {/* Instruction */}
      <Text c="white" fz={16} fw={500} align="center" mt={8}
        style={{ animation: step >= 2 ? "fadeSlideIn 0.4s ease-out" : "none", opacity: step >= 2 ? 1 : 0 }}
      >
        {locale?.transition?.flipPrompt || "Flip your ID to the back side"}
      </Text>
    </Box>
  );
};

// ── Passport detection via MRZ + aspect ratio ──

function analyzePassport(base64Image) {
  try {
    const img = new Image();
    img.src = base64Image;

    // Image must be loaded synchronously (data URIs resolve immediately)
    if (!img.naturalWidth || !img.naturalHeight) {
      return { isPassport: false };
    }

    const w = img.naturalWidth;
    const h = img.naturalHeight;

    // --- Aspect ratio check ---
    const aspectRatio = w / h;
    const aspectRatioPassport = aspectRatio >= 1.3 && aspectRatio <= 1.5;

    // --- MRZ detection via pixel analysis ---
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0);

    // Scan bottom 30% for MRZ bands
    const mrzTop = Math.floor(h * 0.7);
    const mrzHeight = h - mrzTop;
    const imageData = ctx.getImageData(0, mrzTop, w, mrzHeight);
    const pixels = imageData.data;

    // For each row, compute percentage of dark pixels (luma < 80)
    const darkThreshold = 80;
    const minDarkPercent = 0.35;
    let denseRowCount = 0;
    let bandCount = 0;
    let inBand = false;

    for (let row = 0; row < mrzHeight; row++) {
      let darkPixels = 0;
      for (let col = 0; col < w; col++) {
        const idx = (row * w + col) * 4;
        const luma = pixels[idx] * 0.299 + pixels[idx + 1] * 0.587 + pixels[idx + 2] * 0.114;
        if (luma < darkThreshold) darkPixels++;
      }
      const darkPercent = darkPixels / w;

      if (darkPercent >= minDarkPercent) {
        if (!inBand) {
          bandCount++;
          inBand = true;
        }
        denseRowCount++;
      } else {
        inBand = false;
      }
    }

    // TD3 passports have 2 MRZ lines — need at least 2 distinct dense bands
    const mrzDetected = bandCount >= 2 && denseRowCount >= 10;
    const isPassport = mrzDetected || aspectRatioPassport;

    console.log("[analyzePassport] result:", {
      isPassport,
      mrzDetected,
      aspectRatioPassport,
      aspectRatio: aspectRatio.toFixed(3),
      bandCount,
      denseRowCount,
    });

    return { isPassport };
  } catch (err) {
    console.warn("Passport analysis failed:", err);
    return { isPassport: false };
  }
}

// ── Main page component ──

export default function VeratadIdDocCapture() {
  const { data, settings, text, postPayloadMessage } = useContext(DataContext);
  const theme = useMantineTheme();
  const locale = useLocale("document-capture");
  const blockedLocale = useLocale("device-blocked");

  // State machine: setup → captureFront → transition → captureBack → processing
  const [phase, setPhase] = useState("setup");
  const [loading, setLoading] = useState(false);
  const [isSDKComplete, setIsSDKComplete] = useState(false);

  // Capture state
  const [frontImage, setFrontImage] = useState(null);
  const [backImage, setBackImage] = useState(null);
  const [detectedDocType, setDetectedDocType] = useState(null);

  // Capture integrity — preload challenge as early as possible
  const challengeRef = useRef(null);
  const sessionTimestampsRef = useRef({ session_start: Date.now() });

  useEffect(() => {
    if (settings?.request_id && !challengeRef.current) {
      requestCaptureChallenge(settings.request_id)
        .then((c) => { challengeRef.current = c; })
        .catch((err) => console.warn("[CaptureIntegrity] Challenge preload failed:", err));
    }
  }, [settings?.request_id]);

  // Device detection
  const { isTrulyMobile, serverTamperDetected, serverCheckPending } = useDeviceDetection({
    requestId: settings?.request_id,
    serverReportUrl: getDeviceSignalsUrl(),
  });
  const [onMobile, setOnMobile] = useState(isTrulyMobile);
  const [qrScanned, setQrScanned] = useState(false);

  const [showFirefoxPrompt, setShowFirefoxPrompt] = useState(
    isMobileDevice && isFirefox
  );

  const isVeratadSite = settings?.site_name?.toLowerCase().includes("veratad");

  // Build capture settings based on site
  const captureSettings = buildCaptureSettings(settings);

  // Should we skip the back based on settings?
  const frontIdOnly = settings?.journey?.settings?.frontIdOnly === true;

  // Update onMobile when device detection changes
  useEffect(() => {
    setOnMobile(isTrulyMobile);
  }, [isTrulyMobile]);

  useEffect(() => {
    if (data?.isReactNative) setOnMobile(true);
  }, [data?.isReactNative]);

  useEffect(() => {
    if (settings?.journey?.settings?.showHandoffOnTablet && isTablet) {
      setOnMobile(false);
    }
  }, [settings?.journey?.settings?.showHandoffOnTablet]);

  // Status polling (same pattern as dcams/index.js)
  if (data?.edge === true) {
    useEdgeStatusCheck(isSDKComplete);
  } else {
    useStatusCheck(isSDKComplete, false, "id-doc/veratad-capture");
  }

  // When SDK is complete, POST to process endpoint
  useEffect(() => {
    if (!isSDKComplete) return;

    setLoading(true);
    data.settings = settings;
    data.type = "SDK_SCAN";

    const edgeQueueUrl =
      process.env.REACT_APP_EDGE_QUEUE_URL ||
      "https://dcams.app/edge/frontend/api/queue/batch";

    const apiUrl =
      data?.edge === true
        ? edgeQueueUrl
        : "https://vx.dcams.app/frontend/dcams/process";

    const requestBody =
      data?.edge === true ? { ...data, queue: "dcams" } : data;

    // Include detected document type
    if (detectedDocType) {
      requestBody.detected_document_type = detectedDocType;
    }

    fetch(apiUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(requestBody),
    })
      .then((res) => res.json())
      .then(() => setLoading(false))
      .catch((err) => {
        console.error("Process request failed:", err);
        setLoading(false);
      });
  }, [isSDKComplete]);

  // ── Helpers ──

  function dataURItoBlob(dataURI) {
    const byteString = atob(dataURI.split(",")[1]);
    const mimeString = dataURI.split(",")[0].split(":")[1].split(";")[0];
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }
    return new Blob([ab], { type: mimeString });
  }


  // ── Phase handlers ──

  function handleStartCapture() {
    if (showFirefoxPrompt) return;
    setLoading(true);
    setPhase("captureFront");
    sessionTimestampsRef.current.capture_start = Date.now();
  }

  async function handleFrontCapture(imageSrc, docType, conf, frameChain) {
    setFrontImage(imageSrc);
    setDetectedDocType(docType);
    setLoading(true);

    try {
      // Upload immediately — no review step
      const blob = dataURItoBlob(imageSrc);
      const imageKey = `${settings.request_id}_dl_front.jpeg`;
      sessionTimestampsRef.current.capture_end = Date.now();
      await processAndUploadWithIntegrity(
        imageKey, blob, challengeRef.current, settings.request_id,
        "front", frameChain, sessionTimestampsRef.current, settings
      );

      // Determine if we need the back
      const passportAnalysis = analyzePassport(imageSrc);
      const isPassport = passportAnalysis.isPassport || docType === "passport";
      const skipBack = isPassport || frontIdOnly;

      if (skipBack) {
        data.request_id = settings.request_id;
        data.country = settings.country;
        if (!("front_ext" in data)) {
          data.front_ext = "jpeg";
          data.back_ext = "jpeg";
        }
        setLoading(false);
        setPhase("processing");
        setIsSDKComplete(true);

        if (postPayloadMessage) {
          postPayloadMessage({
            event: "VERIFICATION_INITIATED",
            app: "id-doc/veratad-capture",
          });
        }
      } else {
        setLoading(false);
        setPhase("transition");
        // After the transition animation, move to captureBack
        setTimeout(() => setPhase("captureBack"), 4000);
      }
    } catch (err) {
      console.error("Front upload failed:", err);
      // Only reset if the image truly failed to upload — integrity errors
      // are handled inside processAndUploadWithIntegrity with fallback.
      setLoading(false);
      setPhase("setup");
    }
  }

  async function handleBackCapture(imageSrc, docType, conf, frameChain) {
    setBackImage(imageSrc);
    setLoading(true);

    try {
      // Upload immediately — no review step
      const blob = dataURItoBlob(imageSrc);
      const imageKey = `${settings.request_id}_dl_back.jpeg`;
      sessionTimestampsRef.current.capture_end = Date.now();
      await processAndUploadWithIntegrity(
        imageKey, blob, challengeRef.current, settings.request_id,
        "back", frameChain, sessionTimestampsRef.current, settings
      );

      data.request_id = settings.request_id;
      data.country = settings.country;
      if (!("front_ext" in data)) {
        data.front_ext = "jpeg";
        data.back_ext = "jpeg";
      }

      setLoading(false);
      setPhase("processing");
      setIsSDKComplete(true);

      if (postPayloadMessage) {
        postPayloadMessage({
          event: "VERIFICATION_INITIATED",
          app: "id-doc/veratad-capture",
        });
      }
    } catch (err) {
      console.error("Back upload failed:", err);
      setLoading(false);
      setPhase("captureBack");
    }
  }

  function handleCaptureReady() {
    setLoading(false);
  }

  function handleCaptureError(err) {
    console.error("Capture error:", err);
    // Only return to setup for fatal errors (camera failures).
    // Non-fatal warnings (e.g. model_load_warning) should not interrupt capture.
    if (err?.type === "camera_error") {
      setLoading(false);
      setPhase("setup");
    }
  }

  // ── Render guards ──

  if (showFirefoxPrompt) {
    return <FirefoxCameraWarning onContinue={() => setShowFirefoxPrompt(false)} />;
  }

  if (serverCheckPending) {
    return <LoadingOverlay visible />;
  }

  if (serverTamperDetected) {
    return (
      <Box
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          minHeight: "400px",
          padding: "20px",
        }}
      >
        <ThemeIcon variant="light" color="red" size={80} radius="xl">
          <IconShieldX size={50} stroke={1.5} />
        </ThemeIcon>
        <Text fz={24} fw={700} align="center" mt="lg">
          {blockedLocale?.blocked?.title || "Unable to Continue"}
        </Text>
        <Text fz="md" c="dimmed" align="center" mt="xs" maw={400}>
          {blockedLocale?.blocked?.message || "Please try again on your mobile device."}
        </Text>
      </Box>
    );
  }

  // Handoff for desktop
  const showHandoffOnTablet = settings?.journey?.settings?.showHandoffOnTablet === true;
  const shouldShowQROnTablet = showHandoffOnTablet && isTablet;
  const shouldShowContinueOnDesktop = settings?.shouldShowContinueOnDesktop === true;

  if (shouldShowQROnTablet && !onMobile) {
    return (
      <>
        <Handoff nextPath="id-doc/veratad-capture" />
        <Button onClick={() => setOnMobile(true)} mt="sm" fullWidth variant="subtle">
          {text?.qr?.troublePrimaryButton || "Continue on this device"}
        </Button>
      </>
    );
  }

  if (!onMobile && !shouldShowContinueOnDesktop) {
    return <Handoff nextPath="id-doc/veratad-capture" />;
  }

  // ── Phase renders ──

  if (phase === "processing") {
    return <ProcessingScreen locale={locale} isVeratadSite={isVeratadSite} />;
  }

  // Keep DocumentCapture mounted across front→transition→back to preserve
  // the camera stream. The transition screen overlays on top.
  if (phase === "captureFront" || phase === "transition" || phase === "captureBack") {
    const captureSide = phase === "captureFront" ? "front" : "back";
    const isTransitioning = phase === "transition";

    return (
      <>
        {loading && <LoadingOverlay visible />}
        <DocumentCapture
          side={captureSide}
          captureSettings={captureSettings}
          onCapture={captureSide === "front" ? handleFrontCapture : handleBackCapture}
          onError={handleCaptureError}
          onReady={handleCaptureReady}
          paused={isTransitioning}
        />
        {isTransitioning && <FlipTransitionScreen locale={locale} />}
      </>
    );
  }

  // ── Setup screen ──

  const instructions = [
    { Icon: IconSunHigh, text: locale?.setup?.wellLit || "You are in a well-lit area" },
    { Icon: IconId, text: locale?.setup?.flatSurface || "Place ID on a flat, dark surface" },
    { Icon: IconCapture, text: locale?.setup?.allCorners || "All four corners must be visible" },
  ];

  return (
    <>
      {loading && <LoadingOverlay visible />}
      <Box
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          height: "100%",
        }}
      >
        <Box>
          <Stack align="center" spacing="md">
            <ThemeIcon
              variant="light"
              c={data?.styles?.primaryColor ?? theme.primaryColor}
              size={85}
              radius="xl"
            >
              <IconId size={70} stroke={1} />
            </ThemeIcon>
            <Text fz={30} fw={700} align="center">
              {locale?.setup?.title || "ID Document Verification"}
            </Text>
            <Text align="center">
              {locale?.setup?.instructions || "Before you get started ensure the following:"}
            </Text>
            <Paper
              withBorder
              m="md"
              p="md"
              style={{
                backgroundColor:
                  theme.colorScheme === "dark"
                    ? theme.colors.dark[6]
                    : "#fafafa",
                width: "100%",
              }}
            >
              <Stack spacing="xl">
                {instructions.map(({ Icon, text: instrText }, index) => (
                  <Group key={index} noWrap>
                    <Icon size={30} />
                    <Text fz={18}>{instrText}</Text>
                  </Group>
                ))}
              </Stack>
            </Paper>
          </Stack>
        </Box>
        <Box>
          <Text fz="sm" c="dimmed" align="center" pb={15}>
            {locale?.setup?.cameraPermission || "We will ask you to allow camera permissions"}
          </Text>
          <Button size="lg" onClick={handleStartCapture} fullWidth>
            {locale?.setup?.getStarted || "Get Started"}
          </Button>

          {/* Compact handoff for desktop with continue-on-desktop */}
          {!onMobile && shouldShowContinueOnDesktop && !qrScanned && (
            <CompactHandoff
              nextPath="id-doc/veratad-capture"
              onQrScanned={setQrScanned}
            />
          )}
        </Box>
      </Box>
    </>
  );
}

// ── Build capture settings based on site config (mirrors Microblink settings) ──

function buildCaptureSettings(settings) {
  const siteName = settings?.site_name || "";
  const siteNameLower = siteName.toLowerCase();
  const captureSettings = {};

  // Desktop or specific sites: disable blur + glare
  if (
    !isMobileDevice ||
    siteName === "GAVS-PROD-TEST" ||
    siteName === "GAVS-PROD-RO"
  ) {
    captureSettings.blurPolicy = "disabled";
    captureSettings.glarePolicy = "disabled";
  }

  // Veratad sites: use "steady" strategy (responsive capture)
  if (siteNameLower.includes("veratad")) {
    captureSettings.captureStrategy = "snap";
    captureSettings.lightingThresholds = {
      tooDarkThreshold: 10,
    };
  }

  // KID environments: allow darker lighting
  if (["kid-qa", "kid-pre-prod"].includes(siteNameLower)) {
    captureSettings.lightingThresholds = {
      tooDarkThreshold: 10,
    };
  }

  return captureSettings;
}
